import React, { useState, useMemo, useEffect, useRef } from "react";
import { alpha, Box } from "@mui/system";
import {
	Button,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	InputAdornment,
	Menu,
	MenuItem,
	ListItemIcon,
} from "@mui/material";
import useStyles from "../styles";
import { useTheme } from "@emotion/react";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DatagridComponent from "../../../../components/datagridComponent";
import {
	Description,
	HeadphonesOutlined,
	RouterOutlined,
	WifiOutlined,
	CheckCircleOutlineOutlined,
	ReportOutlined,
	CalendarTodayOutlined,
	Add,
	SearchOutlined,
	EditOutlined,
	DeleteOutlined,
	Visibility,
	VisibilityOff,
	HourglassEmptyOutlined,
	RefreshOutlined,
} from "@mui/icons-material";
import CardReport from "./cardReport";
import api from "../../../../services/api";
import InternoModalConfirmacaoSaida from "../../../../components/modalSaindoDaPagina";
import dayjs from "dayjs";

// Intervalo do polling de progresso enquanto houver relatório na fila/em execução
const INTERVALO_POLLING_MS = 5000;

export default function AllReports({
	setComponentValue,
	clienteId,
	usuarioId,
	loadingUsuarios = false,
	setSnackbar = () => {},
	initialDate,
	finalDate,
	onEditReport,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [tabValue, setTabValue] = useState(0);
	const [searchText, setSearchText] = useState("");
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingGerar, setLoadingGerar] = useState(null);
	const [loadingPublicar, setLoadingPublicar] = useState(null);

	// Estados para o Menu de Ações
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedMenuRow, setSelectedMenuRow] = useState(null);
	const openMenu = Boolean(anchorEl);

	const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
	const [deleteLoad, setDeleteLoad] = useState(false);

	// Estados para Confirmação de Publicação
	const [openPublishConfirm, setOpenPublishConfirm] = useState(false);
	const [selectedPublishRow, setSelectedPublishRow] = useState(null);
	const [publishLoad, setPublishLoad] = useState(false);

	const [openPublishAllConfirm, setOpenPublishAllConfirm] = useState(false);
	const [publishAllLoad, setPublishAllLoad] = useState(false);

	const [openUnpublishAllConfirm, setOpenUnpublishAllConfirm] = useState(false);
	const [unpublishAllLoad, setUnpublishAllLoad] = useState(false);

	// Polling de progresso dos jobs de geração
	const pollingRef = useRef(null);

	// ==========================================
	// BUSCA OS RELATÓRIOS DO BACKEND
	// ==========================================
	useEffect(() => {
		if (!loadingUsuarios) {
			fetchRelatorios();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clienteId, usuarioId, loadingUsuarios]);

	// ==========================================
	// PROGRESSO DA GERAÇÃO (polling enquanto houver job na fila/em execução)
	// ==========================================
	const idsEmGeracao = useMemo(
		() => rows.filter((r) => r._raw?.estadoGeracao).map((r) => r.id),
		[rows],
	);
	const chaveIdsEmGeracao = idsEmGeracao.join(",");

	useEffect(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
		if (!chaveIdsEmGeracao) return undefined;

		pollingRef.current = setInterval(async () => {
			try {
				const res = await api.get("/interno/relatorio/progresso", {
					params: { ids: chaveIdsEmGeracao },
				});
				const snapshots = res?.data || [];
				const porId = new Map(snapshots.map((p) => [p.id, p]));

				setRows((atual) =>
					atual.map((r) => {
						const p = porId.get(r.id);
						if (!p) return r;
						return {
							...r,
							status: mapStatus(p.status),
							_raw: {
								...r._raw,
								status: p.status,
								estadoGeracao: p.estadoGeracao,
								progresso: p.progresso,
								etapa: p.etapa,
								relatorioArquivo: p.relatorioArquivo,
								historico: p.historico,
							},
						};
					}),
				);

				const finalizados = snapshots.filter((p) => !p.estadoGeracao);
				if (finalizados.length > 0) {
					const falhas = finalizados.filter((p) => p.status === "FALHA");
					if (falhas.length > 0) {
						setSnackbar({
							severity: "error",
							children: falhas[0]?.erro
								? `Falha na geração: ${falhas[0].erro}`
								: "Falha na geração de um relatório.",
						});
					} else {
						setSnackbar({
							severity: "success",
							children:
								finalizados.length === 1
									? "Relatório gerado — o PDF já pode ser baixado."
									: `${finalizados.length} relatórios gerados — os PDFs já podem ser baixados.`,
						});
					}
					fetchRelatorios();
				}
			} catch (_) {
				// Ignora falhas pontuais do polling para não interromper a UX
			}
		}, INTERVALO_POLLING_MS);

		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
			pollingRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chaveIdsEmGeracao]);

	const fetchRelatorios = async () => {
		try {
			setLoading(true);
			const response = await api.get("/interno/relatorio/findMany", {
				params: {
					...(clienteId && { clienteId }),
					...(usuarioId && { usuarioId }),
				},
			});

			const mapped = response?.data?.map((rel) => ({
				id: rel.id,
				nome: rel.nome,
				cliente: rel.cliente?.nome || "—",
				tipo: rel.tipos?.map((t) => t.tipo).join(", ") || "—",
				frequencia: rel.agendamento?.frequencia || "Único",
				status: mapStatus(rel.status),
				data: formatarData(rel.createdAt),
				dataInicio: formatarData(rel.dataInicial),
				dataFim: formatarData(rel.dataFinal),
				_raw: rel,
			}));

			setRows(mapped || []);
		} catch (error) {
			console.error("Erro ao buscar relatórios:", error);
		} finally {
			setLoading(false);
		}
	};

	// ==========================================
	// GERAR / BAIXAR PDF
	// ==========================================

	// Baixa o PDF salvo em RelatorioArquivo (sem re-gerar)
	const handleBaixarSalvo = async (row) => {
		try {
			setLoadingGerar(row.id);
			const response = await api.get(
				`/interno/relatorio/download/${row.id}`,
				{ responseType: "blob" },
			);
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `relatorio-${row.nome}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (error) {
			console.error("Erro ao baixar arquivo salvo:", error);
		} finally {
			setLoadingGerar(null);
		}
	};

	// Enfileira a geração no backend (coleta direto no banco do Zabbix, em
	// background). O progresso aparece na coluna Status via polling e o PDF
	// fica disponível para download ao concluir — pode sair da página.
	const handleGerar = async (row) => {
		try {
			setLoadingGerar(row.id);
			const response = await api.post("/interno/relatorio/generate", {
				id: row.id,
			});
			setSnackbar({
				severity: "success",
				children:
					response?.data?.message ||
					"Relatório na fila de geração — acompanhe o progresso na tabela.",
			});
			await fetchRelatorios();
		} catch (error) {
			const status = error?.response?.status;
			console.error("Erro ao iniciar a geração:", error);
			setSnackbar({
				severity: status === 409 ? "warning" : "error",
				children:
					error?.response?.data?.message ||
					"Erro ao iniciar a geração do relatório.",
			});
			if (status === 409) fetchRelatorios();
		} finally {
			setLoadingGerar(null);
		}
	};

	// ==========================================
	// PUBLICAR / DESPUBLICAR
	// ==========================================
	const handleTogglePublicar = async (row, novoEstado) => {
		const id = row?.id || row?._raw?.id;
		const relatorioArquivo = row?._raw?.relatorioArquivo ?? row?.relatorioArquivo;
		const publicadoAtual = row?._raw?.publicado ?? row?.publicado;
		const publicar = novoEstado !== undefined ? novoEstado : !publicadoAtual;

		const isAgendado = row?._raw?.status === "AGENDADO" || row?.status === "Agendado";

		if (publicar && !relatorioArquivo && !isAgendado) {
			setSnackbar({
				severity: "warning",
				children: "Gere o PDF antes de publicar para o cliente.",
			});
			return;
		}

		if (publicar) {
			setSelectedPublishRow(row);
			setOpenPublishConfirm(true);
		} else {
			await executePublishToggle(row, false);
		}
	};

	const executePublishToggle = async (row, publicado) => {
		const id = row?.id || row?._raw?.id;
		try {
			setPublishLoad(true);
			setLoadingPublicar(id);
			await api.post("/interno/relatorio/publish", { id, publicado });
			fetchRelatorios();
			setSnackbar({
				severity: "success",
				children: publicado
					? "Relatório publicado para o cliente."
					: "Relatório despublicado.",
			});
			setOpenPublishConfirm(false);
		} catch (error) {
			console.error("Erro ao publicar relatório:", error);
			setSnackbar({ severity: "error", children: "Erro ao publicar relatório." });
		} finally {
			setPublishLoad(false);
			setLoadingPublicar(null);
			setSelectedPublishRow(null);
		}
	};

	const executePublishAll = async () => {
		try {
			setPublishAllLoad(true);
			const promises = publishableReports.map((row) =>
				api.post("/interno/relatorio/publish", { id: row.id, publicado: true })
			);
			await Promise.all(promises);
			fetchRelatorios();
			setSnackbar({
				severity: "success",
				children: `${publishableReports.length} relatório(s) publicado(s) com sucesso.`,
			});
			setOpenPublishAllConfirm(false);
		} catch (error) {
			console.error("Erro ao publicar relatórios:", error);
			setSnackbar({ severity: "error", children: "Erro ao publicar um ou mais relatórios." });
		} finally {
			setPublishAllLoad(false);
		}
	};

	const executeUnpublishAll = async () => {
		try {
			setUnpublishAllLoad(true);
			const promises = unpublishableReports.map((row) =>
				api.post("/interno/relatorio/publish", { id: row.id, publicado: false })
			);
			await Promise.all(promises);
			fetchRelatorios();
			setSnackbar({
				severity: "success",
				children: `${unpublishableReports.length} relatório(s) despublicado(s) com sucesso.`,
			});
			setOpenUnpublishAllConfirm(false);
		} catch (error) {
			console.error("Erro ao despublicar relatórios:", error);
			setSnackbar({ severity: "error", children: "Erro ao despublicar um ou mais relatórios." });
		} finally {
			setUnpublishAllLoad(false);
		}
	};

	// ==========================================
	// AÇÕES DO MENU
	// ==========================================
	const handleOpenMenu = (event, row) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setSelectedMenuRow(row);
	};

	const handleCloseMenu = (event) => {
		if (event) event.stopPropagation();
		setAnchorEl(null);
	};

	const handleEditar = () => {
		if (!selectedMenuRow) return;
		if (onEditReport) {
			onEditReport(selectedMenuRow._raw);
		}
		handleCloseMenu();
	};

	const handleDeletarClick = () => {
		setOpenDeleteConfirm(true);
		setAnchorEl(null);
	};

	const executeDelete = async () => {
		if (!selectedMenuRow) return;
		setDeleteLoad(true);
		try {
			await api.post("/interno/relatorio/delete", { id: selectedMenuRow.id });
			fetchRelatorios();
			setOpenDeleteConfirm(false); // Fecha o modal
			setSelectedMenuRow(null); // Limpa o estado
		} catch (error) {
			console.error("Erro ao deletar:", error);
		} finally {
			setDeleteLoad(false);
		}
	};

	// ==========================================
	// COLUNAS E LAYOUT
	// ==========================================
	const getStatusConfig = (status) => {
		switch (status) {
			case "Concluído":
				return {
					color: "success",
					bgColor: theme.palette.chip.background.success,
					icon: <CheckCircleOutlineOutlined fontSize="small" />,
				};
			case "Em execução":
				return {
					color: "info",
					bgColor: theme.palette.chip.background.medium,
					icon: <CircularProgress size={10} color="inherit" />,
				};
			case "Na fila":
				return {
					color: "info",
					bgColor: theme.palette.chip.background.medium,
					icon: <HourglassEmptyOutlined sx={{ fontSize: "14px !important" }} />,
				};
			case "Falha":
				return {
					color: "error",
					bgColor: theme.palette.chip.background.critical,
					icon: <ReportOutlined fontSize="small" />,
				};
			case "Agendado":
				return {
					color: "warning",
					bgColor: theme.palette.chip.background.high,
					icon: <CalendarTodayOutlined sx={{ fontSize: "14px !important" }} />,
				};
			default:
				return { color: "default", bgColor: "transparent", icon: undefined };
		}
	};

	const getIconByType = (type) => {
		switch (type?.toLowerCase()) {
			case "cpe":
				return <RouterOutlined fontSize="small" />;
			case "onu":
				return <WifiOutlined fontSize="small" />;
			case "atendimento":
				return <HeadphonesOutlined fontSize="small" />;
			default:
				return undefined;
		}
	};

	const columns = [
		{
			field: "nome",
			headerName: "Relatório",
			flex: 1,
			minWidth: 150,
			renderCell: (params) => (
				<Box display="flex" alignItems="center" gap={1} height="100%" width="100%" sx={{ minWidth: 0 }}>
					<Box
						sx={{
							backgroundColor: alpha(theme.palette.primary.main, 0.2),
							borderRadius: "10px",
							width: "35px",
							height: "35px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<Description
							fontSize="small"
							sx={{
								color:
									theme.palette.mode === "dark"
										? theme.palette.primary.light
										: theme.palette.primary.dark,
							}}
						/>
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Tooltip title={params.value} arrow>
							<Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
								{params.value}
							</Typography>
						</Tooltip>
						{params.row._raw?.agendamento && (
							<Typography
								variant="body2"
								sx={{ color: "text.secondary", fontSize: "0.75rem" }}
							>
								Próximo: {formatarData(params.row._raw.agendamento.dataInicial)}
							</Typography>
						)}
						{params.row._raw?.relatorioArquivo?.tamanho && (
							<Typography
								variant="body2"
								sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: 500 }}
							>
								PDF - {formatarTamanhoArquivo(params.row._raw.relatorioArquivo.tamanho)}
							</Typography>
						)}
					</Box>
				</Box>
			),
		},
		{
			field: "cliente",
			headerName: "Cliente",
			width: 200,
			// a célula é flex (sxDatagrid): texto puro quebra linha em vez de reticências
			renderCell: (params) => (
				<Typography variant="body2" noWrap title={params.value}>
					{params.value}
				</Typography>
			),
		},
		{
			field: "tipo",
			headerName: "Tipo",
			width: 130,
			renderCell: (params) => (
				<Chip
					label={params.value}
					size="small"
					variant="outlined"
					icon={getIconByType(params.value)}
					sx={{ minWidth: "80px" }}
				/>
			),
		},
		{ field: "frequencia", headerName: "Frequência", width: 130 },
		{
			field: "status",
			headerName: "Status",
			width: 180,
			renderCell: (params) => {
				const raw = params.row._raw;
				const estado = raw?.estadoGeracao;

				
				if (estado) {
					const progresso = Math.max(
						0,
						Math.min(100, Number(raw?.progresso) || 0),
					);
					const label = estado === "NA_FILA" ? "Na fila" : "Em execução";
					const config = getStatusConfig(label);
					const corPreenchimento =
						theme.palette[config.color]?.main || theme.palette.info.main;
					return (
						<Tooltip title={raw?.etapa || label} arrow>
							<Chip
								variant="outlined"
								label={`${label} · ${progresso}%`}
								color={config.color}
								size="small"
								sx={{
									width: "fit-content",
									fontWeight: 600,
									background: `linear-gradient(to right, ${alpha(corPreenchimento, 0.35)} 0%, ${alpha(corPreenchimento, 0.35)} ${progresso}%, ${config.bgColor} ${progresso}%, ${config.bgColor} 100%)`,
									transition: "background 0.4s ease",
								}}
							/>
						</Tooltip>
					);
				}

				const config = getStatusConfig(params.value);
				const erro =
					raw?.status === "FALHA" ? raw?.historico?.[0]?.erro : null;
				const chip = (
					<Chip
						variant="outlined"
						label={params.value}
						color={config.color}
						icon={config.icon}
						size="small"
						sx={{
							bgcolor: config.bgColor,
							"& .MuiChip-icon": { mt: "-1px", ml: "4px" },
						}}
					/>
				);
				return erro ? (
					<Tooltip title={erro} arrow>
						{chip}
					</Tooltip>
				) : (
					chip
				);
			},
		},
		{
			field: "publicado",
			headerName: "Publicado",
			width: 140,
			renderCell: (params) => {
				const publicado = Boolean(params.row._raw?.publicado);
				return (
					<Chip
						variant="outlined"
						label={publicado ? "Publicado" : "Não publicado"}
						color={publicado ? "success" : "default"}
						size="small"
						sx={{
							bgcolor: publicado
								? alpha(theme.palette.success.main, 0.1)
								: alpha(theme.palette.colors.gray.dark, 0.1),
							fontWeight: 500,
						}}
					/>
				);
			},
		},
		{ field: "data", headerName: "Data de Geração", width: 130 },
		{ field: "dataInicio", headerName: "Data de Início", width: 130 },
		{ field: "dataFim", headerName: "Data Final", width: 130 },
		{
			field: "acoes",
			headerName: "",
			width: 190,
			sortable: false,
			align: "right",
			headerAlign: "right",
			renderCell: (params) => {
				const raw = params.row._raw;
				const gerando = Boolean(raw?.estadoGeracao);
				const isGerando = loadingGerar === params.row.id;
				const temArquivo = Boolean(raw?.relatorioArquivo);
				// Só relatórios com período fixo podem ser (re)gerados manualmente;
				// os agendados sem período são gerados pelo agendamento
				const podeGerar = !gerando && Boolean(raw?.dataInicial && raw?.dataFinal);

				return (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="flex-end"
						height="100%"
						width="100%"
						gap={0.5}
					>
						{temArquivo && !gerando && (
							<Button
								size="small"
								variant="outlined"
								startIcon={
									isGerando ? <CircularProgress size={12} /> : <DownloadIcon />
								}
								disabled={isGerando}
								onClick={(e) => {
									e.stopPropagation();
									handleBaixarSalvo(params.row);
								}}
								sx={{
									textTransform: "none",
									fontWeight: 500,
									borderRadius: "10px",
									bgcolor: alpha(theme.palette.primary.main, 0.1),
									color:
										theme.palette.mode === "dark"
											? theme.palette.primary.light
											: theme.palette.primary.dark,
								}}
							>
								Baixar
							</Button>
						)}
						{podeGerar && (
							<Tooltip title={temArquivo ? "Gerar novamente" : "Gerar PDF"} arrow>
								<IconButton
									size="small"
									disabled={isGerando}
									onClick={(e) => {
										e.stopPropagation();
										handleGerar(params.row);
									}}
								>
									{isGerando ? (
										<CircularProgress size={14} />
									) : (
										<RefreshOutlined fontSize="small" />
									)}
								</IconButton>
							</Tooltip>
						)}
						<IconButton
							size="small"
							onClick={(e) => handleOpenMenu(e, params.row)}
						>
							<MoreVertIcon fontSize="small" />
						</IconButton>
					</Box>
				);
			},
		},
	];

	const totalGerados = rows.filter(
		(r) => r.status === "Concluído" || r.status === "Falha",
	).length;
	const totalAgendados = rows.filter((r) => r.status === "Agendado").length;
	// Inclui os jobs na fila e os agendados em geração (estadoGeracao)
	const totalExecucao = rows.filter(
		(r) => r._raw?.estadoGeracao || r.status === "Em execução",
	).length;
	const totalFalha = rows.filter((r) => r.status === "Falha").length;

	const filteredRows = useMemo(() => {
		let current = rows;
		if (tabValue === 1)
			current = current.filter(
				(r) => r.status === "Concluído" || r.status === "Falha",
			);
		if (tabValue === 2)
			current = current.filter((r) => r.status === "Agendado");

		if (initialDate && finalDate) {
			const start = dayjs(initialDate).startOf("day");
			const end = dayjs(finalDate).endOf("day");
			current = current.filter((r) => {
				const dataToCompare = r._raw?.createdAt;
				if (!dataToCompare) return false;
				const itemDate = dayjs(dataToCompare);
				return (
					(itemDate.isAfter(start) || itemDate.isSame(start)) &&
					(itemDate.isBefore(end) || itemDate.isSame(end))
				);
			});
		}

		if (searchText.trim()) {
			const lower = searchText.toLowerCase();
			current = current.filter((row) =>
				Object.values(row).some((v) => String(v).toLowerCase().includes(lower)),
			);
		}
		return current;
	}, [tabValue, searchText, rows, initialDate, finalDate]);

	const publishableReports = useMemo(() => {
		return filteredRows.filter(
			(row) =>
				((row.status === "Concluído" && row._raw?.relatorioArquivo) || row.status === "Agendado") &&
				!row._raw?.publicado
		);
	}, [filteredRows]);

	const clientNames = useMemo(() => {
		const names = Array.from(new Set(publishableReports.map((r) => r.cliente)));
		return names.join(", ");
	}, [publishableReports]);

	const unpublishableReports = useMemo(() => {
		return filteredRows.filter(
			(row) =>
				((row.status === "Concluído" && row._raw?.relatorioArquivo) || row.status === "Agendado") &&
				row._raw?.publicado
		);
	}, [filteredRows]);

	const unpublishClientNames = useMemo(() => {
		const names = Array.from(new Set(unpublishableReports.map((r) => r.cliente)));
		return names.join(", ");
	}, [unpublishableReports]);

	return (
		<Box
			sx={{
				flexGrow: 1,
				p: 1,
				bgcolor: theme.palette.background.paper,
				borderRadius: "12px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
					p: 1,
					pb: 0,
				}}
			>
				<Typography variant="h5" sx={{ fontWeight: "bold" }}>
					Meus Relatórios
				</Typography>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button
						size="small"
						variant="outlined"
						color="primary"
						startIcon={publishableReports.length > 0 ? <Visibility /> : <VisibilityOff />}
						disabled={publishableReports.length === 0 && unpublishableReports.length === 0}
						onClick={() => {
							if (publishableReports.length > 0) {
								setOpenPublishAllConfirm(true);
							} else {
								setOpenUnpublishAllConfirm(true);
							}
						}}
						sx={{
							textTransform: "none",
							borderRadius: "10px",
							fontWeight: 500,
						}}
					>
						{publishableReports.length > 0 ? "Publicar todos" : "Despublicar todos"}
					</Button>
					<Button
						size="small"
						variant="contained"
						startIcon={<Add />}
						onClick={() => {
							// if (!clienteId) {
							// 	setSnackbar({
							// 		severity: "warning",
							// 		children: "Selecione um cliente para criar um relatório.",
							// 	});
							// 	return;
							// }
							setComponentValue(1);
						}}
					>
						Criar Relatório
					</Button>
				</Box>
			</Box>

			<Box
				sx={{
					bgcolor: theme.palette.background.wallpaper,
					p: 2,
					borderRadius: "12px",
				}}
			>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Grid container spacing={1}>
							<Grid item xs={12} sm={6} md={3}>
								<CardReport
									cardTile="Gerados este mês"
									cardValue={totalGerados}
									status="Gerados"
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<CardReport
									cardTile="Agendados"
									cardValue={totalAgendados}
									status="Agendados"
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<CardReport
									cardTile="Em execução"
									cardValue={totalExecucao}
									status="Em execução"
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<CardReport
									cardTile="Falha"
									cardValue={totalFalha}
									status="Falha"
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid item xs={12}>
						<Box
							sx={{
								bgcolor: theme.palette.background.paper,
								p: 2,
								borderRadius: "12px",
							}}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									borderBottom: 1,
									borderColor: "divider",
									mb: 2,
								}}
							>
								<Tabs
									value={tabValue}
									onChange={(_, v) => setTabValue(v)}
									allowScrollButtonsMobile
									sx={styles.tabs}
								>
									<Tab
										label={
											<Box sx={styles.boxTabs}>
												<Typography>Todos</Typography>
												<Chip
													label={rows.length}
													size="small"
													sx={styles.stylesChips}
												/>
											</Box>
										}
									/>
									<Tab
										label={
											<Box sx={styles.boxTabs}>
												<Typography>Gerados</Typography>
												<Chip
													label={totalGerados}
													size="small"
													sx={styles.stylesChips}
												/>
											</Box>
										}
									/>
									<Tab
										label={
											<Box sx={styles.boxTabs}>
												<Typography>Agendados</Typography>
												<Chip
													label={totalAgendados}
													size="small"
													sx={styles.stylesChips}
												/>
											</Box>
										}
									/>
								</Tabs>

								<TextField
									placeholder="Buscar relatório..."
									variant="outlined"
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchOutlined fontSize="small" />
											</InputAdornment>
										),
									}}
									sx={{
										width: "250px",
										"& .MuiOutlinedInput-root": {
											height: "36px",
											borderRadius: "8px",
										},
										"& .MuiOutlinedInput-input": {
											padding: "8.5px 14px",
											fontSize: "0.875rem",
										},
									}}
								/>
							</Box>

							<Box sx={{ height: 450, width: "100%" }}>
								<DatagridComponent
									rows={filteredRows}
									columns={columns}
									loading={loading || loadingUsuarios}
									sxDatagrid={{
										"& .MuiDataGrid-row": {
											minHeight: "55px !important",
											maxHeight: "55px !important",
										},
										"& .MuiDataGrid-cell": {
											display: "flex",
											alignItems: "center",
										},
									}}
								/>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Box>

			{/* MENU DE OPÇÕES (EDITAR / APAGAR) */}
			<Menu
				anchorEl={anchorEl}
				open={openMenu}
				onClose={handleCloseMenu}
				onClick={(e) => e.stopPropagation()}
				PaperProps={{
					elevation: 3,
					sx: { minWidth: 160, borderRadius: "8px", mt: 0.5 },
				}}
			>
				<MenuItem
					onClick={handleEditar}
					disabled={
						selectedMenuRow?._raw?.status === "AGENDADO" &&
						selectedMenuRow?._raw?.agendamento?.dataInicial &&
						dayjs(selectedMenuRow?._raw?.agendamento?.dataInicial).isBefore(dayjs())
					}
				>
					<ListItemIcon>
						<EditOutlined fontSize="small" />
					</ListItemIcon>
					Editar
				</MenuItem>

				{/* Publicar / Despublicar (somente se tiver arquivo gerado ou se for agendado) */}
				<MenuItem
					onClick={() => {
						handleTogglePublicar(selectedMenuRow);
						handleCloseMenu();
					}}
					disabled={!selectedMenuRow?._raw?.relatorioArquivo && selectedMenuRow?._raw?.status !== "AGENDADO"}
					sx={{
						color: selectedMenuRow?._raw?.publicado ? "warning.main" : "success.main",
					}}
				>
					<ListItemIcon>
						{selectedMenuRow?._raw?.publicado ? (
							<VisibilityOff
								fontSize="small"
								sx={{ color: "warning.main" }}
							/>
						) : (
							<Visibility
								fontSize="small"
								sx={{ color: "success.main" }}
							/>
						)}
					</ListItemIcon>
					{selectedMenuRow?._raw?.publicado
						? "Despublicar"
						: "Publicar para cliente"}
				</MenuItem>

				{/* Apagar */}
				<MenuItem onClick={handleDeletarClick} sx={{ color: "error.main" }}>
					<ListItemIcon>
						<DeleteOutlined fontSize="small" color="error" />
					</ListItemIcon>
					Apagar
				</MenuItem>
			</Menu>

			<InternoModalConfirmacaoSaida
				openConfirmacao={openDeleteConfirm}
				deleteLoad={deleteLoad}
				setOpenConfirmacao={setOpenDeleteConfirm}
				execute={executeDelete}
				severity={"error"}
				title={"Atenção"}
				contentText={
					"Se você prosseguir com a operação, o relatório será deletado permanentemente!"
				}
				confirmText={"Excluir"}
			/>

			<InternoModalConfirmacaoSaida
				openConfirmacao={openPublishConfirm}
				confirmLoad={publishLoad}
				setOpenConfirmacao={setOpenPublishConfirm}
				execute={() => executePublishToggle(selectedPublishRow, true)}
				severity={"warning"}
				title={"Atenção"}
				contentText={
					`Essa ação vai publicar o relatório para o cliente "${selectedPublishRow?.cliente}". Tem certeza de continuar?`
				}
				confirmText={"Confirmar"}
			/>

			<InternoModalConfirmacaoSaida
				openConfirmacao={openPublishAllConfirm}
				confirmLoad={publishAllLoad}
				setOpenConfirmacao={setOpenPublishAllConfirm}
				execute={executePublishAll}
				severity={"warning"}
				title={"Atenção"}
				contentText={
					`Essa ação vai publicar ${publishableReports.length} relatório(s) para o(s) cliente(s) "${clientNames}". Tem certeza de continuar?`
				}
				confirmText={"Publicar todos"}
			/>

			<InternoModalConfirmacaoSaida
				openConfirmacao={openUnpublishAllConfirm}
				confirmLoad={unpublishAllLoad}
				setOpenConfirmacao={setOpenUnpublishAllConfirm}
				execute={executeUnpublishAll}
				severity={"warning"}
				title={"Atenção"}
				contentText={
					`Essa ação vai despublicar ${unpublishableReports.length} relatório(s) para o(s) cliente(s) "${unpublishClientNames}". Tem certeza de continuar?`
				}
				confirmText={"Despublicar todos"}
			/>
		</Box>
	);
}

// ==========================================
// HELPERS
// ==========================================
function mapStatus(status) {
	const map = {
		AGENDADO: "Agendado",
		NA_FILA: "Na fila",
		EM_EXECUCAO: "Em execução",
		CONCLUIDO: "Concluído",
		FALHA: "Falha",
	};
	return map[status] || status;
}

function formatarData(data) {
	if (!data) return "—";
	return new Date(data).toLocaleDateString("pt-BR");
}

function formatarTamanhoArquivo(bytes) {
	if (!bytes || isNaN(bytes)) return "";
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${kb.toFixed(1)} KB`;
	const mb = kb / 1024;
	return `${mb.toFixed(1)} MB`;
}
